import { z } from 'zod';

/** Classroom name for Lýstu orðinu, next to `FIND_FOUR_TITLE`. */
export const DESCRIBE_WORD_TITLE = 'Lýstu orðinu';

/** Server-authoritative turn length, starting after the teacher begins the clock. */
export const DESCRIBE_WORD_TURN_MS = 60_000;

/** How many options a guesser (and the teacher) sees, including the secret. */
export const DESCRIBE_WORD_OPTION_COUNT = 8;

/** Categories offered to the speaker, then words from the chosen category. */
export const DESCRIBE_CHOICE_COUNT = 2;

export const DESCRIBE_CATEGORIES = [
  'dyr',
  'hlutur',
  'stadur',
  'matur',
  'starf',
  'ithrott',
  'fot',
  'nattura',
] as const;

export const describeCategorySchema = z.enum(DESCRIBE_CATEGORIES);
export type DescribeCategory = z.infer<typeof describeCategorySchema>;

export const DESCRIBE_CATEGORY_LABELS: Record<DescribeCategory, string> = {
  dyr: 'Dýr',
  hlutur: 'Hlutur',
  stadur: 'Staður',
  matur: 'Matur',
  starf: 'Starf',
  ithrott: 'Íþrótt',
  fot: 'Föt',
  nattura: 'Náttúra',
};

const LABEL_TO_CATEGORY = new Map(
  DESCRIBE_CATEGORIES.map((id) => [DESCRIBE_CATEGORY_LABELS[id], id] as const),
);

export function describeCategoryLabel(category: DescribeCategory): string {
  return DESCRIBE_CATEGORY_LABELS[category];
}

export function describeTopicFromLabel(label: string | null | undefined): DescribeCategory | null {
  if (label === null || label === undefined) return null;
  return LABEL_TO_CATEGORY.get(label) ?? null;
}

export function normalizeDescribeWord(raw: string): string {
  return raw.trim().normalize('NFC');
}

export function describeWordKey(raw: string): string {
  return normalizeDescribeWord(raw).toLocaleLowerCase('is');
}

/** First spelling wins; later case/spacing variants are dropped. */
export function uniqueDescribeWords(words: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of words) {
    const word = normalizeDescribeWord(raw);
    if (word === '') continue;
    const key = describeWordKey(word);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(word);
  }
  return out;
}

/**
 * Speaker movement: one tile per correct connected guesser, capped at the
 * option count. An empty class (or a solo speaker) scores 0, unlike Snákar og stigar.
 */
export function describeSpeakerTiles(correctCount: number): number {
  return Math.min(DESCRIBE_WORD_OPTION_COUNT, Math.max(0, Math.trunc(correctCount)));
}

export function describeGuesserTiles(correct: boolean): number {
  return correct ? 1 : 0;
}

export const describePenaltiesSchema = z.object({
  usedWord: z.boolean(),
  similarWords: z.boolean(),
  gestures: z.boolean(),
  otherLanguage: z.boolean().default(false),
});

export type DescribePenalties = z.infer<typeof describePenaltiesSchema>;

export const emptyDescribePenalties = (): DescribePenalties => ({
  usedWord: false,
  similarWords: false,
  gestures: false,
  otherLanguage: false,
});

/**
 * Used-the-word zeroes the speaker. Similar words and/or gestures then halve,
 * then quarter if both, each time rounded down. Another language then keeps
 * one third of that intended movement, also rounded down.
 */
export function applyDescribePenalties(tiles: number, flags: DescribePenalties): number {
  if (flags.usedWord) return 0;
  let base = Math.max(0, Math.trunc(tiles));
  if (flags.similarWords && flags.gestures) base = Math.floor(base / 4);
  else if (flags.similarWords || flags.gestures) base = Math.floor(base / 2);
  if (flags.otherLanguage) base = Math.floor(base / 3);
  return base;
}

export interface DescribeRng {
  shuffle<T>(items: readonly T[]): T[];
  pick<T>(items: readonly T[]): T;
}

/** Two random items, or null when the pool is too small. */
export function pickDescribeChoices<T>(items: readonly T[], rng: DescribeRng): T[] | null {
  if (items.length < DESCRIBE_CHOICE_COUNT) return null;
  return rng.shuffle([...items]).slice(0, DESCRIBE_CHOICE_COUNT);
}

/** Eight shuffled words from a category, or null when the list is too short. */
export function dealDescribePack(words: readonly string[], rng: DescribeRng): string[] | null {
  const unique = uniqueDescribeWords(words);
  if (unique.length < DESCRIBE_WORD_OPTION_COUNT) return null;
  return rng.shuffle(unique).slice(0, DESCRIBE_WORD_OPTION_COUNT);
}

/**
 * Eight shuffled options that include `secret`. The pack never marks which one
 * is correct — guessers and the teacher see the same unlabeled list.
 */
export function packDescribeOptions(
  words: readonly string[],
  secret: string,
  rng: DescribeRng,
): string[] | null {
  const unique = uniqueDescribeWords(words);
  const secretKey = describeWordKey(secret);
  const secretWord = unique.find((word) => describeWordKey(word) === secretKey);
  if (secretWord === undefined) return null;
  const rest = unique.filter((word) => describeWordKey(word) !== secretKey);
  if (rest.length < DESCRIBE_WORD_OPTION_COUNT - 1) return null;
  const others = rng.shuffle(rest).slice(0, DESCRIBE_WORD_OPTION_COUNT - 1);
  return rng.shuffle([secretWord, ...others]);
}

/** Always two columns of four, even when the pack is short. */
export function splitDescribeColumns(options: readonly string[]): {
  left: string[];
  right: string[];
} {
  const half = DESCRIBE_WORD_OPTION_COUNT / 2;
  return {
    left: [...options.slice(0, half)],
    right: [...options.slice(half, DESCRIBE_WORD_OPTION_COUNT)],
  };
}

/**
 * If several pawns would finish in one resolution, the speaker wins when they
 * finished; otherwise the earliest in join order.
 */
export function describeFinishWinner(
  finishers: readonly { teamId: string; isSpeaker: boolean; joinIndex: number }[],
): string | null {
  if (finishers.length === 0) return null;
  const speaker = finishers.find((row) => row.isSpeaker);
  if (speaker !== undefined) return speaker.teamId;
  const sorted = [...finishers].sort((a, b) => a.joinIndex - b.joinIndex);
  return sorted[0]?.teamId ?? null;
}

export const describePhaseSchema = z.enum(['idle', 'choose_category', 'review', 'live', 'scoring']);
export type DescribePhase = z.infer<typeof describePhaseSchema>;

export const describeLiveStateSchema = z.object({
  queue: z.array(z.string().uuid()),
  currentIndex: z.number().int().nonnegative(),
  phase: describePhaseSchema,
  category: describeCategorySchema.nullable(),
  secret: z.string().nullable(),
  options: z.array(z.string()).nullable(),
  offeredCategories: z.array(describeCategorySchema).nullable().default(null),
  offeredWords: z.array(z.string()).nullable().default(null),
  closesAt: z.number().int().positive().nullable(),
  guesses: z.record(z.string(), z.string()),
  usedWord: z.boolean().default(false),
  similarWords: z.boolean().default(false),
  gestures: z.boolean().default(false),
  otherLanguage: z.boolean().default(false),
});

export type DescribeLiveState = z.infer<typeof describeLiveStateSchema>;

export const emptyDescribeLiveState = (): DescribeLiveState => ({
  queue: [],
  currentIndex: 0,
  phase: 'idle',
  category: null,
  secret: null,
  options: null,
  offeredCategories: null,
  offeredWords: null,
  closesAt: null,
  guesses: {},
  usedWord: false,
  similarWords: false,
  gestures: false,
  otherLanguage: false,
});

export function parseDescribeLiveState(raw: unknown): DescribeLiveState | null {
  const parsed = describeLiveStateSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export function describeTurnInFlight(phase: DescribePhase): boolean {
  return phase !== 'idle';
}

/** Teacher console: ten unlabeled options, never the secret itself. */
export const describeTeacherViewSchema = z.object({
  phase: describePhaseSchema,
  speakerTeamId: z.string().uuid().nullable(),
  speakerName: z.string().nullable(),
  category: describeCategorySchema.nullable(),
  options: z.array(z.string()).nullable(),
  closesAt: z.number().int().positive().nullable(),
  guessCount: z.number().int().nonnegative(),
  guessers: z.number().int().nonnegative(),
  queue: z.array(z.object({ teamId: z.string().uuid(), name: z.string() })),
  wordPicked: z.boolean().default(false),
  usedWord: z.boolean().default(false),
  similarWords: z.boolean().default(false),
  gestures: z.boolean().default(false),
  otherLanguage: z.boolean().default(false),
});

export type DescribeTeacherView = z.infer<typeof describeTeacherViewSchema>;

/** Phone that scanned the teacher QR: includes the secret. */
export const describeSecretViewSchema = describeTeacherViewSchema.extend({
  secret: z.string().nullable(),
});

export type DescribeSecretView = z.infer<typeof describeSecretViewSchema>;

export const describePlayerViewSchema = z.object({
  role: z.enum(['speaker', 'guesser', 'waiting']),
  phase: describePhaseSchema,
  category: describeCategorySchema.nullable(),
  /** Speaker, choose_category: two random categories. */
  categories: z.array(describeCategorySchema).nullable(),
  /** Speaker, review: two random words from the chosen category, until they pick. */
  words: z.array(z.string()).nullable(),
  /** Speaker only after they pick; omitted/null for everyone else. */
  word: z.string().nullable(),
  /** Guesser (and the room) once a category is chosen; never keyed as the answer. */
  options: z.array(z.string()).nullable(),
  marked: z.string().nullable(),
  closesAt: z.number().int().positive().nullable(),
});

export type DescribePlayerView = z.infer<typeof describePlayerViewSchema>;

export const describeGuessSchema = z.object({
  option: z.string().min(1).max(80),
});

export type DescribeGuessInput = z.infer<typeof describeGuessSchema>;

export const describePickCategorySchema = z.object({
  category: describeCategorySchema,
});

export type DescribePickCategoryInput = z.infer<typeof describePickCategorySchema>;

export const describePickWordSchema = z.object({
  word: z.string().min(1).max(80),
});

export type DescribePickWordInput = z.infer<typeof describePickWordSchema>;

export const describePinSchema = z.string().regex(/^\d{3}$/, 'PIN verður að vera þrír tölustafir.');

export const describeSetPinSchema = z.object({
  pin: describePinSchema,
});

export type DescribeSetPinInput = z.infer<typeof describeSetPinSchema>;

export const describeUnlockSchema = z.object({
  pin: describePinSchema,
});

export type DescribeUnlockInput = z.infer<typeof describeUnlockSchema>;

/** Projector pairing status. Never includes the PIN. */
export const describeRemoteSchema = z.object({
  token: z.string().min(1),
  pinSet: z.boolean(),
  paired: z.boolean(),
});

export type DescribeRemote = z.infer<typeof describeRemoteSchema>;

export const DESCRIBE_WORD_TOO_FEW = `Þessi flokkur hefur færri en ${String(DESCRIBE_WORD_OPTION_COUNT)} orð. Bættu við orðum í Orðabanka áður en umferðin byrjar.`;

export const DESCRIBE_WORD_TOO_FEW_CATEGORIES = `Það þarf tvo flokka með að minnsta kosti ${String(DESCRIBE_WORD_OPTION_COUNT)} orðum hvor. Bættu við orðum í Orðabanka áður en umferðin byrjar.`;

export const DESCRIBE_NEED_TWO_CATEGORIES = 'Veldu að minnsta kosti tvo flokka.';

export const DESCRIBE_WORD_NEED_TEACHER =
  'Kennarinn þarf að skrá sig inn á leynistöðina áður en leikurinn getur haldið áfram.';

/** Keeps catalog order and drops unknown/duplicate ids. */
export function uniqueDescribeCategories(raw: readonly unknown[]): DescribeCategory[] {
  const wanted = new Set<DescribeCategory>();
  for (const item of raw) {
    const parsed = describeCategorySchema.safeParse(item);
    if (parsed.success) wanted.add(parsed.data);
  }
  return DESCRIBE_CATEGORIES.filter((id) => wanted.has(id));
}

/**
 * Categories a Lýstu orðinu game may deal. Missing or empty (games built
 * before the picker) means the full catalog.
 */
export function describeCategoriesFromConfig(raw: unknown): DescribeCategory[] {
  if (!Array.isArray(raw)) return [...DESCRIBE_CATEGORIES];
  const unique = uniqueDescribeCategories(raw);
  return unique.length === 0 ? [...DESCRIBE_CATEGORIES] : unique;
}

export function describeCategoriesFromBoardConfig(raw: unknown): DescribeCategory[] {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return [...DESCRIBE_CATEGORIES];
  }
  if (!('describeCategories' in raw)) return [...DESCRIBE_CATEGORIES];
  return describeCategoriesFromConfig(raw.describeCategories);
}

/** Prefer the session snapshot so a later edit of the game cannot rewrite a live class. */
export function describeCategoriesForSession(
  snapshot: unknown,
  boardConfig: unknown,
): DescribeCategory[] {
  if (snapshot !== null && typeof snapshot === 'object' && !Array.isArray(snapshot)) {
    if ('boardConfig' in snapshot) {
      return describeCategoriesFromBoardConfig(snapshot.boardConfig);
    }
  }
  return describeCategoriesFromBoardConfig(boardConfig);
}
